import React, { FC, ReactNode } from 'react';
import { injectIntl } from 'react-intl';
import { ReactComponent as EnterpriseIcon } from '../../../../../../icons/entities/enterprise.svg';
import { ReactComponent as LawyerIcon } from '../../../../../../icons/entities/lawyer.svg';
import { ReactComponent as LawFirmIcon } from '../../../../../../icons/entities/law-firm.svg';
import { FilterType } from './FilterItem';
import { Connect, PropsFromRedux } from './FiltersProviderContainer';
import { doctrineAnalytics } from '../../../../../utils/analyticsUtils';
import { InjectedIntlProps } from '../../../../../doctrine.types';

const FILTER_TEXT_MAX_LENGTH = 20;

export const makeLabels = (name: string) => {
  return name.length > FILTER_TEXT_MAX_LENGTH
    ? {
        label: `${name.slice(0, FILTER_TEXT_MAX_LENGTH)}…`,
        labelFull: name,
      }
    : {
        label: name,
      };
};

const handleRemoveWithTracking = (handleRemove: () => void, type: string) => {
  return () => {
    doctrineAnalytics('Search RemoveFilterClicked', {
      source: 'filter_box',
      type,
    });
    handleRemove();
  };
};

type Props = {
  children: (filters: Array<FilterType>) => ReactNode;
} & PropsFromRedux &
  InjectedIntlProps;

const RawFiltersProvider: FC<Props> = ({
  date,
  removeDateFilter,
  excludeMoyensAnnexes,
  removeMoyensAnnexesFilter,
  aggregations,
  removeAggregationFilter,
  enterprise,
  removeEnterpriseFilter,
  lawyer,
  removeLawyerFilter,
  lawOffice,
  removeLawOfficeFilter,
  legislation,
  removeLegislationFilter,
  parties,
  removePartiesFilter,
  children,
  intl,
}) => {
  const filters = [];
  if (enterprise)
    filters.push({
      title: enterprise.raisonSociale,
      subTitle: `${enterprise.ville} · ${enterprise.siren}`,
      handleRemove: handleRemoveWithTracking(
        removeEnterpriseFilter,
        'enterprise',
      ),
      icon: EnterpriseIcon,
    });
  if (lawyer)
    filters.push({
      title: lawyer.title,
      subTitle: `${lawyer.barreau} · ${lawyer.sermentDate}`,
      handleRemove: handleRemoveWithTracking(removeLawyerFilter, 'lawyer'),
      icon: LawyerIcon,
    });
  if (lawOffice)
    filters.push({
      label: lawOffice.lawOfficeName,
      handleRemove: handleRemoveWithTracking(
        removeLawOfficeFilter,
        'lawOffice',
      ),
      icon: LawFirmIcon,
    });
  if (legislation)
    legislation.forEach((l) => {
      filters.push({
        ...makeLabels(l.text),
        handleRemove: handleRemoveWithTracking(
          () => removeLegislationFilter(l.articleId),
          'legislation',
        ),
      });
    });
  if (date)
    filters.push({
      ...makeLabels(date),
      handleRemove: handleRemoveWithTracking(removeDateFilter, 'date'),
    });
  if (excludeMoyensAnnexes)
    filters.push({
      label: intl.formatMessage({
        id:
          'search/components/search-snippets/aside/filter-box/FiltersProvider.tsx/1',
      }),
      handleRemove: handleRemoveWithTracking(
        removeMoyensAnnexesFilter,
        'moyens_annexes',
      ),
    });
  aggregations.forEach((agg) => {
    filters.push({
      ...makeLabels(agg.label),
      handleRemove: handleRemoveWithTracking(
        () => removeAggregationFilter(agg.path),
        agg.path[0],
      ),
    });
  });
  if (parties)
    filters.push({
      ...makeLabels(parties),
      handleRemove: handleRemoveWithTracking(removePartiesFilter, 'parties'),
    });

  return <>{children(filters)}</>;
};

export const FiltersProvider = Connect(injectIntl(RawFiltersProvider));
